import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Clock, AlertCircle, Users, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import ConsistencyLogo from '@/components/ConsistencyLogo';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPricingPage() {
  const [plans, setPlans] = useState([]);
  const [launchState, setLaunchState] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [stripeEnv, setStripeEnv] = useState('test');
  const [stripeReady, setStripeReady] = useState(false);
  const [foundingSeatsRemaining, setFoundingSeatsRemaining] = useState(0);
  const [foundingSeatCap, setFoundingSeatCap] = useState(200);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/pricing-config`);
      const data = await res.json();
      setPlans(data.plans || []);
      setLaunchState(data.launch_state || null);
      setStripeEnv(data.stripe_env || 'test');
      setStripeReady(data.stripe_ready || false);
      setFoundingSeatsRemaining(data.founding_seats_remaining || 0);
      setFoundingSeatCap(data.founding_seat_cap || 200);
    } catch (error) {
      console.error('Error loading pricing:', error);
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleChoosePlan = (plan) => {
    if (!plan.stripe_ready) {
      toast.info('This plan is not yet available for purchase.');
      return;
    }
    localStorage.setItem('selected_plan', plan.plan_key);
    localStorage.setItem('selected_billing', billingInterval);
    window.location.href = '/signup';
  };

  const getDisplayPrice = (plan) => {
    return billingInterval === 'annual' ? plan.annual_price : plan.monthly_price;
  };

  const getBannerContent = () => {
    if (!launchState) return null;
    
    const banner = launchState.banner;
    if (!banner) return null;

    const bannerStyles = {
      prelaunch: 'bg-purple-500/10 border-purple-500/30 text-purple-300',
      launch: 'bg-pink-500/10 border-pink-500/30 text-pink-300',
      trial: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
      standard: 'bg-white/5 border-white/10 text-[#8A93A6]',
    };

    // Enhanced banner with promo message and seats info
    return (
      <div className={`mb-8 p-4 rounded-lg border ${bannerStyles[banner.type] || bannerStyles.standard}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5" />
            <div>
              <p className="font-medium text-white">{banner.title}</p>
              <p className="text-sm opacity-80">{banner.message}</p>
              {banner.promo_message && (
                <p className="text-sm font-medium mt-1 flex items-center gap-1">
                  <Percent className="h-4 w-4" />
                  {banner.promo_message}
                </p>
              )}
            </div>
          </div>
          {/* Founding seats remaining (only during LAUNCH) */}
          {launchState?.phase === 'LAUNCH' && foundingSeatsRemaining > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg">
              <Users className="h-4 w-4 text-yellow-400" />
              <span className="text-sm font-medium text-white">
                {foundingSeatsRemaining} founding seats left
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121417] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-[#8A93A6]">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121417]">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <ConsistencyLogo variant="full" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <Badge 
              variant="outline" 
              className={`text-xs ${
                stripeEnv === 'live' 
                  ? 'border-green-500/30 text-green-400' 
                  : 'border-white/10 text-[#8A93A6]'
              }`}
            >
              {stripeEnv === 'live' ? '🔴 Live' : '🧪 Test Mode'}
            </Badge>
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/signup">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-[#8A93A6] max-w-2xl mx-auto">
            Transform your live streams into a complete content machine. Start free, upgrade anytime.
          </p>
          {launchState && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-[#8A93A6]">
              <Zap className="h-4 w-4 text-pink-500" />
              <span>
                Phase: {launchState.phase} • Day {launchState.day_number}
                {launchState.active_coupon && ` • Coupon: ${launchState.active_coupon}`}
              </span>
            </div>
          )}
        </div>

        {/* Launch Banner */}
        {getBannerContent()}

        {/* Billing Toggle */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              billingInterval === 'monthly'
                ? 'bg-[#3A7BFF] text-white'
                : 'bg-white/5 text-[#8A93A6] hover:bg-white/10'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('annual')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              billingInterval === 'annual'
                ? 'bg-[#3A7BFF] text-white'
                : 'bg-white/5 text-[#8A93A6] hover:bg-white/10'
            }`}
          >
            Annual
            <span className="ml-2 text-xs text-green-400">Save 17%</span>
          </button>
        </div>

        {/* Plan Cards - Rendered from Backend Data (5 Paid Plans Only) */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {plans.map((plan) => (
            <Card 
              key={plan.plan_key} 
              className={`glass border ${
                plan.popular 
                  ? 'border-pink-500/40 ring-1 ring-pink-500/20' 
                  : 'border-white/10'
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl text-white">{plan.display_name}</CardTitle>
                  <div className="flex gap-1">
                    {plan.popular && (
                      <Badge className="bg-pink-500 text-white text-xs">Popular</Badge>
                    )}
                    {plan.launch_pricing_active && (
                      <Badge variant="outline" className="text-xs border-pink-500/40 text-pink-400">
                        Launch
                      </Badge>
                    )}
                  </div>
                </div>
                <CardDescription className="text-[#8A93A6]">
                  {plan.features?.[0] || 'Core features to get started'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Price Display */}
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white">
                    {plan.price_display?.[billingInterval] || `$${getDisplayPrice(plan) ?? 0}`}
                  </span>
                  {plan.plan_key !== 'free' && getDisplayPrice(plan) > 0 && (
                    <span className="text-sm text-[#8A93A6]">
                      /{billingInterval === 'annual' ? 'year' : 'month'}
                    </span>
                  )}
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {plan.trial_enabled && (
                    <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-400">
                      <Clock className="h-3 w-3 mr-1" />
                      7-Day Trial
                    </Badge>
                  )}
                  {plan.earn_free_month_enabled && (
                    <Badge variant="outline" className="text-xs border-green-500/40 text-green-400">
                      Earn Free Month
                    </Badge>
                  )}
                  {plan.founding_seat_active && (
                    <Badge variant="outline" className="text-xs border-yellow-500/40 text-yellow-400">
                      Founding Member
                    </Badge>
                  )}
                </div>

                {/* Features List */}
                <div className="space-y-2 min-h-[100px]">
                  {plan.features?.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-[#8A93A6]">
                      <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button - All plans are paid, stripe_ready should be true */}
                <Button 
                  className="w-full"
                  onClick={() => handleChoosePlan(plan)}
                  disabled={!plan.stripe_ready}
                >
                  {plan.cta_label || `Get ${plan.display_name}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {plans.length === 0 && (
          <div className="text-center py-16">
            <AlertCircle className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No Plans Available</h3>
            <p className="text-[#8A93A6]">
              Pricing plans are being configured. Please check back soon.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
